import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "@/helper/api";
import GenericErrorMessageModal from "@/components/forms/errorHandling/genericErrorMessageModal";
import { useTranslation } from "react-i18next";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import useConfirmOnLeave from "@/helper/customHooks/useConfirmOnLeave";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import { Typography } from "@mui/material";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import styles from "@/components/forms/translation/entityTranslationSubmitForm.module.css";
import RoundedContentBox from "@/components/general/roundedContentBox";
import {
  Controller,
  type FieldErrors,
  type FieldValues,
  useForm,
} from "react-hook-form";
import GenericNotification from "@/components/notification/genericNotification";
import CustomErrorMessage from "@/components/forms/inputsWithRef/customErrorMessage";
import { ErrorMessage } from "@hookform/error-message";
import { TextAreaWithRef } from "@/components/forms/inputsWithRef/textAreaWithRef";
import TinyMceEditor from "@/components/tinymce/tinymce";
import { getLmsCourseOverviewRoute } from "@/components/lms/lms.utils";
import {
  buildTranslationPhraseMap,
  collectTranslationLanguages,
  getDuplicateTranslationTarget,
  isTranslationFieldEmpty,
  isTranslationValueEmpty,
  omitEmptyTranslationFields,
} from "@/components/forms/translation/entityTranslationSubmitForm.utils";

const translationFields: Record<string, string[]> = {
  examination: ["name", "description", "external_title"],
  probe: ["name", "description", "external_title"],
  form: ["name", "widget_title"],
  measurementDefinition: [
    "short_label",
    "label",
    "observation",
    "assessment",
    "possible_remedy",
    "value_map",
  ],
  todoTemplate: ["description", "title"],
  course: ["title", "description"],
  mainCategory: ["name", "description"],
  todo: ["title", "description"],
  folder: ["name"],
};

const richTextFieldsByEntity: Record<string, readonly string[]> = {
  course: ["description"],
};

const getEntityDisplayName = (data: any): string => {
  const displayValue = data?.title ?? data?.name ?? data?.id ?? "";

  if (displayValue && typeof displayValue === "object") {
    const fallbackValue =
      displayValue.de ??
      displayValue.en ??
      Object.values(displayValue)[0] ??
      "";
    return String(fallbackValue);
  }

  return String(displayValue);
};

const EntityTranslationSubmitForm: FC = () => {
  const { entity, entityId } = useParams() as {
    entity: string;
    entityId: string;
  };

  const { t } = useTranslation();
  const heading = t("translation_entity", { entity: t(`${entity}`) });
  usePageHeading(heading);
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo =
    new URLSearchParams(location.search).get("returnTo") ===
    "/settings/missingTranslations"
      ? "/settings/missingTranslations"
      : null;
  const jsonValuePathsRef = useRef<Set<string>>(new Set());
  const {
    handleSubmit,
    control,
    reset,
    register,
    setError,
    clearErrors,
    watch,
    getFieldState,
    trigger,
    formState: { isDirty, errors, isSubmitted },
  } = useForm({
    shouldFocusError: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasUnsavedChanges = !isSubmitting && isDirty;
  useConfirmOnLeave(hasUnsavedChanges, "unsaved_formChanges_confirm");

  const [fullDataResponse, setFullDataResponse] = useState<any>([]);
  const [secondaryHeading, setSecondaryHeading] = useState<string>("");
  const [languages, setLanguages] = useState<any>([]);
  const [transformedRowData, setTransformedRowData] = useState<any[]>([]);
  const [tempSubmitDataToEnforce, setTempSubmitDataToEnforce] =
    useState<any>(null);
  const [localError, setLocalError] = useState<any>(null);
  const [errorCode, setErrorCode] = useState<string>("");

  const [successForNotification, setSuccessForNotification] =
    useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();

  const resetStateOnCloseNotification = () => {
    setSuccessForNotification(false);
  };
  const getNavigationPath = (entity: string, fullDataResponse: any): string => {
    switch (entity) {
      case "measurementDefinition":
        return "/settings/measurementDefinitionList";
      case "probe":
        return "/settings/probeList";
      case "examination":
        return "/settings/examinationList";
      case "form":
        return `/settings/probeEditor/${fullDataResponse?.probe?.id}`;
      case "todoTemplate":
        return "/settings/todoTemplateList";
      case "folder":
        return "/settings/documentEditor";
      case "course":
        return getLmsCourseOverviewRoute(
          "administrator",
          String(fullDataResponse?.id ?? entityId)
        );
      default:
        return "/";
    }
  };

  const transformToTranslationRows = (
    data: any,
    entityType: string
  ): { rows: any[]; langs: string[] } => {
    const fields = translationFields[entityType] || [];
    const langs = collectTranslationLanguages(data, fields);

    const rows = fields.map((field) => {
      const value = data[field];
      const row: Record<string, string> = { id: field };

      langs.forEach((lang) => {
        row[lang] = "";
      });

      if (typeof value === "object" && value !== null) {
        Object.entries(value).forEach(([lang, val]) => {
          row[lang] = (val as string) ?? "";
        });
      } else {
        row.de = value ?? "";
      }

      return row;
    });

    return { rows, langs };
  };

  const formatValueForInput = (path: string, value: any) => {
    if (value !== null && typeof value === "object") {
      jsonValuePathsRef.current.add(path);
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }

    return value ?? "";
  };

  useEffect(() => {
    if (!entity || !entityId) return;

    api.genericApiRequest({
      method: "get",
      entity: entity,
      entityId: entityId,
      parametersToRender: {
        additionalUrlParameters: { show_translation: true },
      },
      successHandler: (res: any) => {
        const data = res.data;
        setFullDataResponse(data);

        setSecondaryHeading(getEntityDisplayName(data));

        const { rows, langs } = transformToTranslationRows(data, entity);

        jsonValuePathsRef.current.clear();
        const initialValues: Record<string, Record<string, string>> = {};
        rows.forEach((row) => {
          initialValues[row.id] = {};
          langs.forEach((lang) => {
            const path = `${row.id}.${lang}`;
            initialValues[row.id][lang] = formatValueForInput(
              path,
              row[lang] ?? ""
            );
          });
        });
        reset(initialValues);
        setTransformedRowData(rows);
        setLanguages(langs);
      },
      failHandler: (error: any) => {
        setLocalError(error);
      },
    });
  }, [entity, entityId, reset]);

  const fieldOrder = useMemo(
    () =>
      transformedRowData.flatMap((row) =>
        languages.map((lang: string) => `${row.id}.${lang}`)
      ),
    [languages, transformedRowData]
  );
  const { focusFirstInvalidField, registerValidationTarget } =
    useFocusFirstInvalidField<FieldValues, string>({
      fieldOrder,
    });

  const getTranslationCellMessage = (
    fieldName: string,
    language: string,
    formValues: Record<string, any>
  ) => {
    const translations = formValues[fieldName];
    if (
      !translations ||
      typeof translations !== "object" ||
      isTranslationFieldEmpty(translations, languages)
    ) {
      return true;
    }
    if (isTranslationValueEmpty(translations[language])) {
      return t("field_required");
    }

    const duplicate = getDuplicateTranslationTarget(
      fieldName,
      language,
      formValues
    );
    if (duplicate) {
      return t("translation_onchange_duplicate_error", {
        language: duplicate.sourceLanguage.toUpperCase(),
        phrase: duplicate.phrase,
      });
    }
    return true;
  };

  const handleInvalidSubmit = (fieldErrors: FieldErrors) => {
    focusFirstInvalidField(fieldErrors);
  };

  const revalidateTranslationField = (fieldName: string) => {
    if (!isSubmitted) {
      return;
    }
    languages.forEach((language: string) => {
      void trigger(`${fieldName}.${language}`);
    });
  };

  useEffect(() => {
    const outlet = document.getElementById("rootOutletContent");
    if (!outlet) return;

    outlet.classList.add("autoHeight");

    return () => {
      outlet.classList.remove("autoHeight");
    };
  }, []);

  const onSubmit = (data: any) => {
    const dataToSubmit: any = {
      ...omitEmptyTranslationFields(
        data,
        transformedRowData.map((row) => row.id),
        languages
      ),
      translations_enabled: true,
    };

    const invalidJsonPaths: string[] = [];
    fieldOrder
      .filter((path) => jsonValuePathsRef.current.has(path))
      .forEach((path) => {
        const [field, lang] = path.split(".");
        const raw = dataToSubmit?.[field]?.[lang];
        if (typeof raw !== "string") return;

        try {
          dataToSubmit[field][lang] = JSON.parse(raw);
        } catch {
          setError(path, {
            type: "validate",
            message: t("invalid_json"),
          });
          invalidJsonPaths.push(path);
        }
      });
    if (invalidJsonPaths.length > 0) {
      focusFirstInvalidField({}, invalidJsonPaths);
      return;
    }

    setIsSubmitting(true);
    api.genericApiRequest({
      method: "put",
      entity: entity,
      entityId: entityId,
      submitData: dataToSubmit,
      successHandler: () => {
        setNotificationMessage(t("save_successful"));
        setNotificationVariant("success");
        const path = returnTo ?? getNavigationPath(entity, fullDataResponse);

        navigate(path);
      },
      failHandler: (error: any, status: any) => {
        setIsSubmitting(false);
        setLocalError(error);
        setErrorCode(status);
        setTempSubmitDataToEnforce(dataToSubmit);
      },
    });
  };

  const enforceSubmitOnConflict = () => {
    setLocalError("");
    setIsSubmitting(true);
    const dataToEnforce = { ...tempSubmitDataToEnforce, force: true };

    const additionalUrlParameters: any = {};
    additionalUrlParameters["force"] = "true";
    api.genericApiRequest({
      method: "put",
      entity: entity,
      entityId: entityId,
      submitData: dataToEnforce,
      successHandler: () => {
        setNotificationMessage(t("save_successful"));
        setNotificationVariant("success");

        const path = returnTo ?? getNavigationPath(entity, fullDataResponse);

        navigate(path);
      },
      failHandler: (error: any, status: any) => {
        setIsSubmitting(false);
        setLocalError(error);
        setErrorCode(status);
      },
    });
  };

  const handleNavigateBack = () => {
    if (returnTo) {
      navigate(returnTo);
      return;
    }

    navigate(-1);
  };

  const validateTranslationConsistency = (changedValue?: {
    path: string;
    value: string;
  }) => {
    const watchedValues = watch();
    const values = changedValue
      ? (() => {
          const [field, language] = changedValue.path.split(".");
          return {
            ...watchedValues,
            [field]: {
              ...watchedValues[field],
              [language]: changedValue.value,
            },
          };
        })()
      : watchedValues;
    const phraseMap = buildTranslationPhraseMap(values);

    let hasError = false;

    for (const key in values) {
      const translations = values[key];
      if (!translations || typeof translations !== "object") {
        continue;
      }

      for (const language of Object.keys(translations)) {
        const duplicate = getDuplicateTranslationTarget(
          key,
          language,
          values,
          phraseMap
        );
        if (duplicate) {
          setError(`${key}.${language}`, {
            type: "duplicate",
            message: t("translation_onchange_duplicate_error", {
              language: duplicate.sourceLanguage.toUpperCase(),
              phrase: duplicate.phrase,
            }),
          });
          hasError = true;
        } else if (
          getFieldState(`${key}.${language}`).error?.type === "duplicate"
        ) {
          clearErrors(`${key}.${language}`);
        }
      }
    }

    return !hasError;
  };

  const typedErrors = errors as Record<
    string,
    Record<string, { message?: string }>
  >;
  return (
    <>
      <RoundedContentBox>
        <form
          onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
          className={styles.formContainer}
        >
          <div className={styles.topTableButtonWrapper}>
            <Typography color="textSecondary">{secondaryHeading}</Typography>
          </div>

          <table className={styles.table}>
            <colgroup>
              <col className={styles.fieldNameColumn} />
              {languages.map((lang: any) => (
                <col key={lang} className={styles.languageColumn} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th className={styles.tableHeaderFieldName} />
                {languages.map((lang: any) => (
                  <th key={lang} className={styles.tableHeaderLanguage}>
                    {lang.toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {transformedRowData.map((row) => (
                <tr key={row.id}>
                  <td className={styles.tableDataFieldName}>
                    {t(`${row.id}`)}
                  </td>
                  {languages.map((lang: any) => {
                    const fieldPath = `${row.id}.${lang}`;
                    const usesRichTextEditor =
                      richTextFieldsByEntity[entity]?.includes(row.id) ?? false;

                    return (
                      <td key={lang} className={styles.tableDataLanguage}>
                        <div
                          className={styles.tableDataLanguageWrapper}
                          ref={registerValidationTarget(fieldPath)}
                        >
                          {usesRichTextEditor ? (
                            <Controller
                              name={fieldPath}
                              control={control}
                              rules={{
                                validate: (_value, formValues) =>
                                  getTranslationCellMessage(
                                    row.id,
                                    lang,
                                    formValues
                                  ),
                              }}
                              render={({ field }) => (
                                <TinyMceEditor
                                  value={field.value ?? ""}
                                  onChange={(content) => {
                                    field.onChange(content);
                                    validateTranslationConsistency({
                                      path: fieldPath,
                                      value: content,
                                    });
                                    revalidateTranslationField(row.id);
                                  }}
                                  onBlur={() => {
                                    field.onBlur();
                                    validateTranslationConsistency();
                                  }}
                                  showFooter={false}
                                  height={300}
                                />
                              )}
                            />
                          ) : (
                            <TextAreaWithRef
                              {...register(fieldPath, {
                                validate: (_value, formValues) =>
                                  getTranslationCellMessage(
                                    row.id,
                                    lang,
                                    formValues
                                  ),
                                onBlur: () => validateTranslationConsistency(),
                                onChange: (event) => {
                                  validateTranslationConsistency({
                                    path: fieldPath,
                                    value: event.target.value,
                                  });
                                  revalidateTranslationField(row.id);
                                },
                              })}
                              rows={4}
                              hideLabel
                              fieldStyle={{
                                resize: "vertical",
                                borderColor: typedErrors?.[row.id]?.[lang]
                                  ? "#d32f2f"
                                  : undefined,
                              }}
                            />
                          )}

                          <div className={styles.tableDataLanguageErrorMessage}>
                            <ErrorMessage
                              errors={errors}
                              name={fieldPath}
                              render={({ message }) => (
                                <CustomErrorMessage
                                  error={"error"}
                                  errorMessage={message}
                                />
                              )}
                            />
                            {/* {typedErrors?.[row.id]?.[lang]?.message || "\u00A0"} */}
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className={styles.bottomButtonWrapper}>
            <GenericButton
              variant="outlined"
              onClick={handleNavigateBack}
              type="button"
            >
              {t("back")}
            </GenericButton>
            <GenericButton type="submit">{t("save")}</GenericButton>
          </div>
        </form>
      </RoundedContentBox>

      {localError && (
        <GenericErrorMessageModal
          title={t("translation_force_modal_title")}
          error={localError}
          status={errorCode}
          errorEntity="entityTranslation"
          confirmButtonText={t("translation_force_button_title")}
          onClosehandler={() => {
            setLocalError("");
            setErrorCode("");
          }}
          onSubmitHandler={enforceSubmitOnConflict}
        />
      )}
      <GenericNotification
        message={successForNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </>
  );
};

export default EntityTranslationSubmitForm;
